import { findImageCredit } from "../lib/image-credits";

/**
 * Small photo-attribution pill for hero banners (RegionPage,
 * CollectionDetailPage). Renders nothing when the image has no entry in
 * lib/image-credits.ts — Unsplash placeholders and generated imagery stay
 * caption-free. Full TASL attribution lives on /credits; this is the
 * in-context courtesy credit. Place inside a position:relative container,
 * after the gradient overlay.
 */
export function ImageCredit({
  src,
  placement = "pill",
}: {
  src: string | null | undefined;
  /**
   * "pill": the bottom-right link pill on hero banners. "corner": a very
   * small, faint top-right caption for image cards that are themselves
   * links (a span, not an anchor, so it can nest inside the card).
   */
  placement?: "pill" | "corner";
}) {
  const credit = findImageCredit(src);
  if (!credit) return null;
  const label = `${credit.title} by ${credit.author} (${credit.license}${credit.modifications ? `, ${credit.modifications}` : ""})`;
  if (placement === "corner") {
    return (
      <span
        title={label}
        style={{
          position: "absolute",
          top: 8,
          right: 10,
          zIndex: 2,
          fontFamily: "var(--font-sans)",
          fontSize: 9.5,
          lineHeight: 1.3,
          color: "rgba(255,255,255,.62)",
          textShadow: "0 1px 2px rgba(0,0,0,.7)",
          whiteSpace: "nowrap",
          pointerEvents: "none",
        }}
      >
        {credit.author} · {credit.license}
      </span>
    );
  }
  return (
    <a
      href={credit.sourceUrl}
      target="_blank"
      rel="noreferrer"
      title={label}
      style={{
        position: "absolute",
        right: 10,
        bottom: 8,
        zIndex: 1,
        padding: "3px 9px",
        borderRadius: 999,
        border: "1px solid var(--border-subtle)",
        background: "color-mix(in srgb, var(--bg-app) 72%, transparent)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        color: "var(--text-secondary)",
        fontFamily: "var(--font-sans)",
        fontSize: 11,
        lineHeight: 1.4,
        whiteSpace: "nowrap",
        textDecoration: "none",
      }}
    >
      Photo: {credit.author} ({credit.license})
    </a>
  );
}
